import { useEffect, useState, type ReactElement } from "react";
import { WarningOutlined } from "@ant-design/icons";
import BiologicalHeading from "./Heading/BiologicalHeading";
import { useCreateProtocolForm } from "../../..";
import AxiosInstance from "../../../../../shared/utils/axios";
import { useSearchParams } from "react-router-dom";
import ApplicationComponent from "./Applications/ApplicationComponent";
import SelectCpProduct from "./Product/cp-product/SelectCpProduct";
import { useProtocolsRouter } from "../../../../../shared/custom-hooks/useProtocolsRouter";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../../shared/utils/translationUtils";

const ProductsBiologicalComponents = (): ReactElement => {
  const { t } = useTranslation();
  const { formValidationData } = useCreateProtocolForm();
  const [existingData, setExistingData] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const protocolId = searchParams.get("protocol_id");
  const [loading, setLoading] = useState<boolean>(true);
  const [isUpadated, setIsUpdated] = useState<boolean>(false);
  const { workspaceId, orgId } = useProtocolsRouter();

  const getProtocolData = async (): Promise<any> => {
    try {
      const response = await AxiosInstance.get(
        `/protocol/${protocolId as string}`,
        {
          headers: {
            "org-id": orgId ?? "",
            "workspace-id": workspaceId ?? "",
          },
        }
      );
      setExistingData(response?.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    getProtocolData().catch((error) => {
      console.error("Error fetching data:", error);
    });
  }, []);

  // `index.tsx`'s own protocol/template loader already writes the
  // `biologicals` form field once (see its `fetchProtocolData`/
  // `fetchTemplateData` effects). This component fetches the SAME
  // `/protocol/{id}` endpoint independently purely to derive `bioCountry`/
  // `orgId` for `SelectCpProduct` below -- it must NOT also write to the
  // `biologicals` field itself, or it races that other write (whichever
  // resolves second wins, and it has no revalidation awareness at all, so
  // it can silently overwrite a correctly-populated selection with its
  // empty-treatments default if the two fetches ever return inconsistent
  // snapshots).
  //
  // Depend on `loading` as well as `existingData`: the effect reads `loading`
  // but not `existingData`, and on the error path only `setLoading(false)`
  // runs (`existingData` stays null), so keying solely on `existingData` could
  // leave `isUpadated` false forever and `SelectCpProduct` stuck loading.
  useEffect(() => {
    if (!loading) {
      setIsUpdated(true);
    }
  }, [existingData, loading]);

  // #1: After a template-driven country change (Scenario A), BasicInfo stores
  // the NEW country's ISO code in `revalidationCountry`. The persisted
  // `crop_details.country` still holds the OLD value until the protocol is
  // saved, so we must prefer the revalidation value to fetch the correct
  // biological/CP catalog. Falls back to persisted country otherwise.
  const bioCountry =
    formValidationData?.revalidationCountry ??
    existingData?.crop_details?.country ??
    null;

  return (
    <div className="products-biologicals-component">
      <BiologicalHeading />

      {Boolean(formValidationData?.showCountryChangeAlert) && (
        <div className="change-country-alert">
          <div className="change-country-alert__header">
            <WarningOutlined className="change-country-alert__icon" />
            <span className="change-country-alert__title">
              {formatTranslation(
                t("createProtocol.basicInfo.changeCountryAlert")
              )}
            </span>
          </div>
          <p className="change-country-alert__body">
            {formatTranslation(
              t("createProtocol.basicInfo.changeCountryAlertBody")
            )}
          </p>
        </div>
      )}

      <SelectCpProduct
        loading={!isUpadated}
        country={bioCountry ?? ""}
        orgId={existingData?.org_id}
      />
      {/* <SelectBiologicalProduct
        loading={!isUpadated}
        country={bioCountry ?? ""}
        orgId={existingData?.org_id}
      /> */}
      {/* <MixRateApplicationVolume loading={!isUpadated} /> */}

      <ApplicationComponent loading={!isUpadated} />
    </div>
  );
};

export default ProductsBiologicalComponents;
